/*
 * Catálogo de calendario preventivo (vacunas, desparasitaciones, controles) por
 * país y especie, y el motor que genera el "Carnet Sanitario": cruza el catálogo
 * con el historial real de la mascota y calcula qué está hecho, qué falta y las
 * alertas de vencimiento (VENCIDO / Próx. Mes / etc).
 *
 * Extraído de src/components/PetProfiles.tsx (versión clásica) para poder
 * reutilizar exactamente la misma lógica en la nueva propuesta
 * (src/screens/PetEnvironment.tsx) sin duplicar el catálogo a mano.
 */

export interface VaccinePlanItem {
    months: number;
    name: string;
    type: 'vacuna' | 'desparasitacion' | 'control' | 'examen' | 'fármaco';
    interval?: number;
    importance: 'low' | 'medium' | 'high' | 'critical';
    note?: string;
}

export const VACCINE_PLANS_GLOBAL: Record<string, any> = {
    // CHILE (Ley Cholito)
    'CL': {
        countryName: 'Chile 🇨🇱',
        'Perro': [
            // CACHORRO: 2 MESES
            { months: 2, name: 'Sextuple/Óctuple (1ra Dosis)', type: 'vacuna', importance: 'high' },
            { months: 2, name: 'Desparasitación Interna', type: 'desparasitacion', interval: 3, importance: 'medium' },
            { months: 2, name: 'Desparasitación Externa', type: 'desparasitacion', interval: 1, importance: 'medium' },

            // CACHORRO: 3 MESES
            { months: 3, name: 'Sextuple/Óctuple (2da Dosis)', type: 'vacuna', importance: 'high' },

            // CACHORRO: 4 MESES
            { months: 4, name: 'Óctuple (3ra Dosis)', type: 'vacuna', importance: 'medium' },
            { months: 4, name: 'Antirrábica (Obligatoria)', type: 'vacuna', importance: 'critical', interval: 12 },

            // JUVENIL: 6 MESES
            { months: 6, name: 'KC (Tos de las Perreras)', type: 'vacuna', importance: 'low', interval: 12 },

            // ADULTO (Refuerzos)
            { months: 12, name: 'Refuerzo Sextuple Anual', type: 'vacuna', interval: 12, importance: 'high' },
        ],
        'Gato': [
            // CACHORRO: 2 MESES
            { months: 2, name: 'Triple Felina (1ra Dosis)', type: 'vacuna', importance: 'high' },
            { months: 2, name: 'Test VIF/ViLeF (SIDA/Leucemia)', type: 'examen', importance: 'critical' },
            { months: 2, name: 'Desparasitación Interna', type: 'desparasitacion', interval: 3, importance: 'medium' },
            { months: 2, name: 'Desparasitación Externa', type: 'desparasitacion', interval: 1, importance: 'medium' },

            // CACHORRO: 3 MESES
            { months: 3, name: 'Triple Felina (2da Dosis)', type: 'vacuna', importance: 'high' },

            // CACHORRO: 4 MESES
            { months: 4, name: 'Leucemia (1ra Dosis)', type: 'vacuna', importance: 'medium' },
            { months: 4, name: 'Antirrábica (Obligatoria)', type: 'vacuna', importance: 'critical', interval: 12 },

            // CACHORRO: 5 MESES
            { months: 5, name: 'Leucemia (2da Dosis)', type: 'vacuna', importance: 'medium' },

            // ADULTO (Refuerzos)
            { months: 12, name: 'Refuerzo Triple Felina', type: 'vacuna', interval: 12, importance: 'high' },
            { months: 12, name: 'Refuerzo Leucemia', type: 'vacuna', interval: 12, importance: 'medium' },
        ],
        'Otro': [
            { months: 6, name: 'Control Sano Anual', type: 'control', interval: 12, importance: 'medium' },
            { months: 0, name: 'Desparasitación Interna', type: 'desparasitacion', interval: 6, importance: 'medium' }
        ],
        // --- PEQUEÑOS MAMÍFEROS ---
        'Hurón': [
            { months: 2, name: 'Moquillo (Distemper)', type: 'vacuna', importance: 'critical' },
            { months: 3, name: 'Moquillo (Refuerzo)', type: 'vacuna', importance: 'critical' },
            { months: 4, name: 'Antirrábica', type: 'vacuna', importance: 'critical', interval: 12 },
            { months: 12, name: 'Refuerzo Moquillo Anual', type: 'vacuna', interval: 12, importance: 'high' },
            { months: 0, name: 'Desparasitación Externa (Pipeta)', type: 'desparasitacion', interval: 1, importance: 'medium' }
        ],
        'Conejo': [
            { months: 2, name: 'Mixomatosis (Si disponible)', type: 'vacuna', importance: 'medium' },
            { months: 2, name: 'Enf. Hemorrágica (Si disponible)', type: 'vacuna', importance: 'medium' },
            { months: 3, name: 'Chequeo Dental Inicial', type: 'control', importance: 'high' },
            { months: 0, name: 'Desparasitación Interna (Coccidios)', type: 'desparasitacion', interval: 6, importance: 'medium' },
            { months: 6, name: 'Control Dental Semestral', type: 'control', interval: 6, importance: 'critical' }
        ],
        'Erizo de Tierra': [
            { months: 2, name: 'Chequeo Ácaros/Piel', type: 'control', importance: 'high' },
            { months: 0, name: 'Desparasitación Externa', type: 'desparasitacion', interval: 3, importance: 'high' },
            { months: 6, name: 'Control Sano Semestral', type: 'control', interval: 6, importance: 'medium' }
        ],
        'Cobaya': [
            { months: 2, name: 'Chequeo Dental', type: 'control', importance: 'high' },
            { months: 0, name: 'Suplemento Vitamina C', type: 'fármaco', interval: 1, importance: 'critical' },
            { months: 0, name: 'Desparasitación Externa', type: 'desparasitacion', interval: 3, importance: 'medium' }
        ],
        'Hámster': [
            { months: 0, name: 'Desparasitación Preventiva', type: 'desparasitacion', interval: 6, importance: 'low' },
            { months: 6, name: 'Chequeo General', type: 'control', interval: 6, importance: 'medium' }
        ],
        'Chinchilla': [
            { months: 6, name: 'Chequeo Dental', type: 'control', interval: 6, importance: 'critical' },
            { months: 12, name: 'Baño de Arena (Revisión)', type: 'control', interval: 1, importance: 'medium' }
        ],
        'Rata Doméstica': [
            { months: 6, name: 'Chequeo Respiratorio/Tumores', type: 'control', interval: 6, importance: 'high' },
            { months: 0, name: 'Desparasitación Externa', type: 'desparasitacion', interval: 3, importance: 'medium' }
        ],
        'Jerbo': [
            { months: 6, name: 'Control Sano', type: 'control', interval: 12, importance: 'medium' }
        ],
        'Degú': [
            { months: 6, name: 'Chequeo Dental y Glucosa', type: 'control', interval: 6, importance: 'critical' },
            { months: 0, name: 'Desparasitación', type: 'desparasitacion', interval: 6, importance: 'medium' }
        ],
        // --- EXÓTICOS Y DE GRANJA ---
        'Minipig': [
            { months: 2, name: 'Erisipela Porcina', type: 'vacuna', importance: 'high' },
            { months: 2, name: 'Parvovirosis', type: 'vacuna', importance: 'high' },
            { months: 0, name: 'Desparasitación Interna', type: 'desparasitacion', interval: 3, importance: 'high' },
            { months: 0, name: 'Limpieza de Pezuñas', type: 'control', interval: 2, importance: 'medium' }
        ],
        'Tortuga de Tierra': [
            { months: 0, name: 'Desparasitación (Nematodos)', type: 'desparasitacion', interval: 6, importance: 'high' },
            { months: 12, name: 'Chequeo Pre-Hibernación', type: 'control', interval: 12, importance: 'critical' }
        ],
        'Tortuga de Agua': [
            { months: 6, name: 'Revisión Caparazón/Hongos', type: 'control', interval: 6, importance: 'high' },
            { months: 12, name: 'Suplemento Calcio/UVB', type: 'control', interval: 12, importance: 'medium' }
        ],
        'Iguana': [
            { months: 0, name: 'Control Metabólico Oseo', type: 'control', interval: 6, importance: 'critical' },
            { months: 0, name: 'Desparasitación', type: 'desparasitacion', interval: 6, importance: 'medium' }
        ],
        'Gecko': [
            { months: 0, name: 'Control de Muda (Piel)', type: 'control', interval: 3, importance: 'high' },
            { months: 0, name: 'Examen Coproparasitario', type: 'examen', interval: 6, importance: 'medium' }
        ],
        'Serpiente': [
            { months: 0, name: 'Control de Muda', type: 'control', interval: 3, importance: 'high' },
            { months: 0, name: 'Desparasitación Externa (Ácaros)', type: 'desparasitacion', interval: 6, importance: 'medium' }
        ],
        // --- AVES ---
        'Ninfa/Catita': [
            { months: 0, name: 'Desparasitación Interna', type: 'desparasitacion', interval: 6, importance: 'medium' },
            { months: 12, name: 'Corte de Uñas/Pico', type: 'control', interval: 6, importance: 'low' }
        ],
        'Loro': [
            { months: 0, name: 'Test Psitacosis', type: 'examen', importance: 'high' },
            { months: 6, name: 'Desparasitación', type: 'desparasitacion', interval: 6, importance: 'medium' },
            { months: 0, name: 'Control Conductual', type: 'control', interval: 12, importance: 'medium' }
        ],
        'Canario': [
            { months: 0, name: 'Control Ácaros Sacos Aéreos', type: 'desparasitacion', interval: 3, importance: 'high' }
        ],
        'Gallina': [
            { months: 2, name: 'Vacuna Newcastle', type: 'vacuna', importance: 'high' },
            { months: 0, name: 'Desparasitación Interna', type: 'desparasitacion', interval: 3, importance: 'high' }
        ],
        'Caballo': [
            { months: 4, name: 'Influenza Equina', type: 'vacuna', interval: 6, importance: 'high' },
            { months: 4, name: 'Tétanos', type: 'vacuna', interval: 12, importance: 'critical' },
            { months: 4, name: 'Herpesvirus', type: 'vacuna', interval: 6, importance: 'medium' },
            { months: 0, name: 'Desparasitación (Paste)', type: 'desparasitacion', interval: 3, importance: 'high' },
            { months: 6, name: 'Limpieza Dental (Limado)', type: 'control', interval: 12, importance: 'medium' }
        ]
    },
    'AR': {
        countryName: 'Argentina 🇦🇷',
        'Perro': [
            { months: 2, name: 'Quíntuple (1ra Dosis)', type: 'vacuna', importance: 'high' },
            { months: 2, name: 'Desparasitación', type: 'desparasitacion', interval: 3, importance: 'medium' },
            { months: 3, name: 'Séxtuple + Tos Perreras', type: 'vacuna', importance: 'high' },
            { months: 4, name: 'Antirrábica', type: 'vacuna', importance: 'critical', interval: 12 },
            { months: 12, name: 'Refuerzo Anual Séxtuple', type: 'vacuna', interval: 12, importance: 'high' },
            { months: 12, name: 'Refuerzo Anual Antirrábica', type: 'vacuna', interval: 12, importance: 'critical' }
        ],
        'Gato': [
            { months: 2, name: 'Triple Felina', type: 'vacuna', importance: 'high' },
            { months: 3, name: 'Refuerzo Triple Felina', type: 'vacuna', importance: 'high' },
            { months: 4, name: 'Antirrábica', type: 'vacuna', importance: 'critical' }
        ]
    },
    // ESPAÑA (UE)
    'ES': {
        countryName: 'España 🇪🇸',
        'Perro': [
            { months: 2, name: 'Polivalente (C)', type: 'vacuna', importance: 'high' },
            { months: 3, name: 'Polivalente (R)', type: 'vacuna', importance: 'high' },
            { months: 4, name: 'Antirrábica (Obligatoria)', type: 'vacuna', importance: 'critical', note: 'Pasaporte Europeo Requerido' },
            { months: 6, name: 'Leishmaniosis (Test + Vacuna)', type: 'vacuna', importance: 'critical', note: 'Endémica en zona mediterránea' },
            { months: 12, name: 'Recuerdo Leishmaniosis', type: 'vacuna', interval: 12, importance: 'critical' },
            { months: 12, name: 'Refuerzo Polivalente', type: 'vacuna', interval: 12, importance: 'high' }
        ],
        'Gato': [
            { months: 2, name: 'Trivalente', type: 'vacuna', importance: 'high' },
            { months: 4, name: 'Antirrábica (Según CC.AA.)', type: 'vacuna', importance: 'medium' }
        ]
    },
    // GENERAL / MEXICO / LATAM
    'LATAM': {
        countryName: 'Internacional 🌎',
        'Perro': [
            { months: 2, name: 'Puppy DP (Parvo/Distemper)', type: 'vacuna', importance: 'high' },
            { months: 3, name: 'Múltiple Viral', type: 'vacuna', importance: 'high' },
            { months: 4, name: 'Antirrábica', type: 'vacuna', importance: 'critical' },
            { months: 6, name: 'Bordetella/Giardia (Opc)', type: 'vacuna', importance: 'low' },
            { months: 12, name: 'Refuerzo Anual Múltiple + Rabia', type: 'vacuna', interval: 12, importance: 'high' }
        ],
        'Gato': [
            { months: 2, name: 'Triple Viral Felina', type: 'vacuna', importance: 'high' },
            { months: 12, name: 'Refuerzo Anual', type: 'vacuna', interval: 12, importance: 'high' }
        ],
        'Otro': [
            { months: 6, name: 'Control Dental (Exóticos)', type: 'control', importance: 'high', note: 'Conejos/Cobayas: Revisión molares' },
            { months: 12, name: 'Control General + Desparasitación', type: 'control', interval: 12, importance: 'medium' }
        ]
    }
};

/** Devuelve el plan regional (por país); LATAM como respaldo genérico. */
export const getVaccinePlan = (countryCode: string = 'CL') => {
    return VACCINE_PLANS_GLOBAL[countryCode] || VACCINE_PLANS_GLOBAL['LATAM'];
};

/**
 * Normaliza el valor de "species" de una mascota a una clave válida del catálogo.
 * Necesario porque hay dos formularios de alta con vocabularios distintos:
 * la clásica guarda nombres en español ("Perro", "Conejo", ...) y la nueva
 * propuesta guarda códigos en inglés ("Dog", "Cat", "Bird", "Other").
 */
export const speciesToPlanKey = (species: string | undefined | null, countryCode: string = 'CL'): string => {
    const s = (species || '').trim().toLowerCase();
    if (s === 'dog' || s === 'perro') return 'Perro';
    if (s === 'cat' || s === 'gato') return 'Gato';
    const plan = getVaccinePlan(countryCode);
    const exactKey = Object.keys(plan).find(k => k.toLowerCase() === s);
    return exactKey || 'Otro';
};

/** Etiqueta de edad legible ("3 Meses", "1 Años 2 Meses") entre dos fechas. */
export const getAgeLabel = (birthDate: Date, targetDate: Date): string => {
    let years = targetDate.getFullYear() - birthDate.getFullYear();
    let months = targetDate.getMonth() - birthDate.getMonth();
    if (months < 0 || (months === 0 && targetDate.getDate() < birthDate.getDate())) {
        years--;
        months += 12;
    }
    if (years === 0) return `${months} Meses`;
    return `${years} Años ${months} Meses`;
};

export interface VaccineHistoryRecord {
    id?: string | number;
    date: string;
    place?: string;
    attachment?: string | null;
    attachmentName?: string;
}

export interface VaccineScheduleRow extends VaccinePlanItem {
    uniqueKey: string;
    targetDate: string;
    realDate?: string;
    ageLabel: string;
    isDone: boolean;
    place?: string;
    attachment?: string | null;
    attachmentName?: string;
    historyIndex?: number;
    diffDays: number;
    alertType?: 'none' | 'warning' | 'danger';
    alertMsg?: string;
}

/**
 * Motor del Carnet Sanitario: cruza el catálogo preventivo de la especie con el
 * historial real de la mascota y devuelve las filas ordenadas cronológicamente,
 * cada una marcada como realizada (con su fecha real) o pendiente (con la fecha
 * objetivo calculada y su alerta: VENCIDO / "En X días" / "Próx. Mes" / Pendiente).
 *
 * Idéntico al cálculo usado en la versión clásica (PetProfiles.tsx) para que
 * ambas interfaces muestren exactamente el mismo calendario.
 */
export function buildVaccineSchedule(
    birth: Date,
    speciesOrPlanKey: string,
    vaccineStatus: Record<string, VaccineHistoryRecord[]>,
    countryCode: string = 'CL'
): VaccineScheduleRow[] {
    try {
        if (!birth || isNaN(birth.getTime())) return [];

        const planKey = speciesToPlanKey(speciesOrPlanKey, countryCode);
        const regionalData = getVaccinePlan(countryCode);
        const basePlan: VaccinePlanItem[] = regionalData[planKey] || regionalData['Otro'] || [];

        let rows: VaccineScheduleRow[] = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (Array.isArray(basePlan)) {
            basePlan.forEach((item) => {
                if (!item) return;
                const history = vaccineStatus[item.name] || [];

                // 1. SIEMPRE mostrar los registros históricos (YA HECHOS)
                history.forEach((record, index) => {
                    rows.push({
                        ...item,
                        uniqueKey: `${item.name}-${index}`,
                        targetDate: record.date,
                        realDate: record.date,
                        ageLabel: getAgeLabel(birth, new Date(record.date)),
                        isDone: true,
                        place: record.place,
                        attachment: record.attachment,
                        attachmentName: record.attachmentName,
                        historyIndex: index,
                        diffDays: 0,
                    });
                });

                // 2. Calcular la PRÓXIMA TAREA (PENDIENTE)
                let nextTargetDate: Date;

                if (item.interval) {
                    const lastRecordDate = history.length > 0
                        ? history.reduce<Date | null>((max, r) => {
                            const d = new Date(String(r.date).slice(0, 10) + 'T23:59:59');
                            return !isNaN(d.getTime()) && (!max || d > max) ? d : max;
                        }, null)
                        : null;

                    nextTargetDate = new Date(birth);
                    nextTargetDate.setMonth(nextTargetDate.getMonth() + (item.months || 0));

                    if (lastRecordDate) {
                        while (nextTargetDate <= lastRecordDate) {
                            nextTargetDate.setMonth(nextTargetDate.getMonth() + item.interval);
                        }
                    }

                    const ageMonths = (today.getFullYear() - birth.getFullYear()) * 12 + (today.getMonth() - birth.getMonth());
                    const isAdult = ageMonths >= 12;

                    if (isAdult && nextTargetDate < today) {
                        while (nextTargetDate < today) {
                            nextTargetDate.setMonth(nextTargetDate.getMonth() + item.interval);
                        }
                    }
                } else {
                    if (history.length === 0) {
                        nextTargetDate = new Date(birth);
                        nextTargetDate.setMonth(nextTargetDate.getMonth() + (item.months || 0));
                    } else {
                        // Ya se hizo (única vez) y ya se agregó en el paso 1.
                        return;
                    }
                }

                if (isNaN(nextTargetDate.getTime())) return;

                const targetDateStr = nextTargetDate.toISOString().split('T')[0];
                const diffTime = nextTargetDate.getTime() - today.getTime();
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                let alertType: 'none' | 'warning' | 'danger' = 'none';
                let alertMsg = 'Pendiente';

                // Roja desde 1 día antes de la puesta (antes solo se ponía roja
                // el mismo día/vencida) — amarilla desde 7 días antes.
                if (diffDays <= 1 && diffDays > -365) { alertType = 'danger'; alertMsg = diffDays <= 0 ? 'VENCIDO' : 'Mañana'; }
                else if (diffDays <= 1) { alertType = 'danger'; alertMsg = 'NO REALIZADO'; }
                else if (diffDays <= 7) { alertType = 'warning'; alertMsg = `En ${diffDays} d`; }
                else if (diffDays <= 30) { alertType = 'none'; alertMsg = 'Próx. Mes'; }

                rows.push({
                    ...item,
                    uniqueKey: `${item.name}-pending`,
                    targetDate: targetDateStr,
                    ageLabel: getAgeLabel(birth, nextTargetDate),
                    isDone: false,
                    place: '--',
                    alertType,
                    alertMsg,
                    diffDays,
                });
            });
        }

        // 3. Ordenar cronológicamente (mezcla historial con tareas futuras)
        rows.sort((a, b) => new Date(a.targetDate).getTime() - new Date(b.targetDate).getTime());

        return rows;
    } catch (err) {
        console.error('Error calculating vaccine schedule', err);
        return [];
    }
}

/**
 * Convierte la respuesta cruda de `api.getVaccines(petId)`
 * ({ id, name, date_applied, place, attachment_url, attachment_name }[])
 * en el mapa agrupado por nombre que espera `buildVaccineSchedule`.
 */
export function groupVaccineHistory(rawVaccines: any[]): Record<string, VaccineHistoryRecord[]> {
    const statusMap: Record<string, VaccineHistoryRecord[]> = {};
    if (!Array.isArray(rawVaccines)) return statusMap;
    rawVaccines.forEach((v: any) => {
        if (!v || !v.name) return;
        if (!statusMap[v.name]) statusMap[v.name] = [];
        const date = String(v.date_applied || v.dateApplied || '').slice(0, 10);
        // Mismo nombre + misma fecha = la misma aplicación registrada dos
        // veces (doble toque / reintentos): se muestra una sola.
        if (statusMap[v.name].some(r => String(r.date).slice(0, 10) === date)) return;
        statusMap[v.name].push({
            id: v.id,
            date: v.date_applied || v.dateApplied,
            place: v.place,
            attachment: v.attachment_url || v.attachment || null,
            attachmentName: v.attachment_name || v.attachmentName,
        });
    });
    // Siempre de la más antigua a la más reciente: la API las puede devolver
    // en cualquier orden y el cálculo de "próxima dosis" toma la última.
    Object.values(statusMap).forEach(list => list.sort((a, b) => String(a.date).localeCompare(String(b.date))));
    return statusMap;
}
